import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService from '../../services/api';
import { marketingService } from '../../services/marketingService';
import SEOCategoryGauges from './SEOCategoryGauges';

const SEOWrapper = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
`;

const SEOHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  
  h2 {
    margin: 0 0 var(--spacing-2) 0;
    color: var(--color-secondary);
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }
  
  p {
    color: var(--color-text-light);
    margin: 0;
  }
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  
  .tab {
    padding: var(--spacing-4) var(--spacing-6);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    color: var(--color-text-light);
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    
    &.active {
      color: var(--color-secondary);
      border-bottom-color: var(--color-secondary);
    }
    
    &:hover {
      color: var(--color-secondary);
    }
  }
`;

const TabContent = styled.div`
  padding: var(--spacing-6);
`;

const SEOForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-6);
`;

const FormSection = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-6);
  
  h4 {
    margin: 0 0 var(--spacing-4) 0;
    color: var(--color-secondary);
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    font-size: var(--font-size-lg);
  }
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-4);
  
  &:last-child {
    margin-bottom: 0;
  }
  
  label {
    display: block;
    margin-bottom: var(--spacing-2);
    font-weight: 500;
    color: var(--color-text);
  }
  
  input, textarea, select {
    width: 100%;
    padding: var(--spacing-3);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius);
    font-size: var(--font-size-sm);
    
    &:focus {
      outline: none;
      border-color: var(--color-secondary);
      box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
    }
  }
  
  textarea {
    min-height: 80px;
    resize: vertical;
    font-family: inherit;
  }
  
  select {
    cursor: pointer;
  }
  
  .char-count {
    font-size: var(--font-size-xs);
    text-align: right;
    margin-top: var(--spacing-1);
    
    &.warning { color: #f59e0b; }
    &.error { color: #dc2626; }
    &.good { color: #10b981; }
  }
  
  .help-text {
    font-size: var(--font-size-xs);
    color: var(--color-text-light);
    margin-top: var(--spacing-1);
    line-height: 1.4;
  }
`;

const SEOPreview = styled.div`
  background: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  margin-top: var(--spacing-4);
  
  h5 {
    margin: 0 0 var(--spacing-3) 0;
    color: var(--color-secondary);
    font-size: var(--font-size-sm);
  }
  
  .preview-title {
    color: #1a0dab;
    font-size: var(--font-size-lg);
    font-weight: 400;
    margin-bottom: var(--spacing-1);
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .preview-url {
    color: #006621;
    font-size: var(--font-size-sm);
    margin-bottom: var(--spacing-2);
  }
  
  .preview-description {
    color: #4d5156;
    font-size: var(--font-size-sm);
    line-height: 1.4;
  }
`;

const SaveButton = styled.button`
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-3) var(--spacing-6);
  border: none;
  border-radius: var(--border-radius);
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  
  &:hover {
    background-color: #1f1e3a;
  }
  
  &:disabled {
    background-color: var(--color-text-light);
    cursor: not-allowed;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--spacing-4);
  margin-top: var(--spacing-6);
`;

const ProductCard = styled.div<{ hasSEO?: boolean }>`
  border: 1px solid ${props => props.hasSEO ? 'var(--color-secondary)' : 'var(--color-border)'};
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  transition: all 0.2s ease;
  background: ${props => props.hasSEO ? '#faf5ff' : 'white'};
  
  &:hover {
    border-color: var(--color-secondary);
    box-shadow: var(--shadow-sm);
  }
  
  .product-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-3);
    
    .product-info {
      flex: 1;
      
      .product-name {
        font-weight: 500;
        color: var(--color-secondary);
        margin-bottom: var(--spacing-1);
      }
      
      .product-url {
        font-size: var(--font-size-xs);
        color: var(--color-text-light);
      }
    }
    
    .seo-badge {
      padding: 2px 8px;
      border-radius: 12px;
      font-size: var(--font-size-xs);
      font-weight: 500;
      
      &.has-seo {
        background: #dcfce7;
        color: #166534;
      }
      
      &.no-seo {
        background: #fef3c7;
        color: #92400e;
      }
    }
  }
  
  .edit-btn {
    background: var(--color-secondary);
    color: white;
    border: none;
    padding: var(--spacing-2) var(--spacing-3);
    border-radius: var(--border-radius);
    font-size: var(--font-size-xs);
    cursor: pointer;
    margin-top: var(--spacing-2);
    
    &:hover {
      background-color: #1f1e3a;
    }
  }
  
  .seo-preview {
    font-size: var(--font-size-sm);
    color: var(--color-text-light);
    
    .title {
      color: var(--color-secondary);
      margin-bottom: var(--spacing-1);
      font-weight: 500;
    }
    
    .description {
      line-height: 1.4;
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-12);
  color: var(--color-text-light);
`;

const ErrorWrapper = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  color: #dc2626;
  margin-bottom: var(--spacing-4);
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-4);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
`;

const ModalHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    color: var(--color-secondary);
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: var(--font-size-xl);
    cursor: pointer;
    color: var(--color-text-light);
    
    &:hover {
      color: var(--color-text);
    }
  }
`;

const ModalBody = styled.div`
  padding: var(--spacing-6);
`;

const ModalFooter = styled.div`
  padding: var(--spacing-6);
  border-top: 1px solid var(--color-border);
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  
  button {
    padding: var(--spacing-3) var(--spacing-4);
    border: none;
    border-radius: var(--border-radius);
    font-weight: 500;
    cursor: pointer;
    
    &.primary {
      background-color: var(--color-secondary);
      color: white;
      
      &:hover {
        background-color: #1f1e3a;
      }
      
      &:disabled {
        background-color: var(--color-text-light);
        cursor: not-allowed;
      }
    }
    
    &.secondary {
      background-color: var(--color-border);
      color: var(--color-text);
      
      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const CategorySection = styled.div`
  margin-bottom: var(--spacing-8);
  
  h4 {
    margin: 0 0 var(--spacing-4) 0;
    color: var(--color-secondary);
    font-size: var(--font-size-lg);
    padding: var(--spacing-3) var(--spacing-4);
    background: linear-gradient(135deg, var(--color-secondary) 0%, #1f1e3a 100%);
    color: white;
    border-radius: var(--border-radius);
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
`;

const SEOStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-8);
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-4);
  text-align: center;

  .stat-number {
    font-size: var(--font-size-2xl);
    font-weight: 700;
    color: var(--color-secondary);
  }

  .stat-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-light);
    margin-top: var(--spacing-1);
  }

  .stat-icon {
    font-size: 2.5rem;
    margin-bottom: var(--spacing-3);
  }

  .stat-content {
    display: flex;
    flex-direction: column;
    align-items: center;

    .stat-help {
      font-size: var(--font-size-xs);
      color: var(--color-text-light);
      opacity: 0.7;
      margin-top: var(--spacing-1);
    }
  }
`;

interface SEOSettings {
  id?: number;
  entity_type: string;
  entity_id: number;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  canonical_url: string;
  og_title: string;
  og_description: string;
  og_image: string;
  robots_meta: string;
}

interface MarketingPagesSectionProps {
  onEditSEO?: (page: any) => void;
}

const MarketingPagesSection: React.FC<MarketingPagesSectionProps> = ({ onEditSEO }) => {
  const [marketingPages, setMarketingPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMarketingPages();
  }, []);

  const loadMarketingPages = async () => {
    try {
      setLoading(true);
      const pages = await marketingService.getAll();
      setMarketingPages(pages);
    } catch (error) {
      console.error('Error loading marketing pages:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Laddar marknadsföringssidor...</div>;
  }

  return (
    <div>
      {marketingPages.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 'var(--spacing-8)',
          background: '#f8f9fa',
          borderRadius: 'var(--border-radius)',
          border: '2px dashed #dee2e6'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-4)' }}>📄</div>
          <h3 style={{ color: 'var(--color-text-light)', marginBottom: 'var(--spacing-2)' }}>
            Inga marknadsföringssidor skapade än
          </h3>
          <p style={{ color: 'var(--color-text-light)', marginBottom: 'var(--spacing-4)' }}>
            Skapa din första marknadsföringssida i fliken "Marknadsföringssidor" först.
          </p>
        </div>
      ) : (
        <ProductGrid>
          {marketingPages.map((page) => (
            <ProductCard key={page.id}>
              <div style={{ padding: 'var(--spacing-4)' }}>
                <h4 style={{ margin: '0 0 var(--spacing-2) 0', color: 'var(--color-secondary)' }}>
                  {page.title}
                </h4>
                <p style={{
                  color: 'var(--color-text-light)',
                  fontSize: 'var(--font-size-sm)',
                  margin: '0 0 var(--spacing-2) 0',
                  fontFamily: 'monospace'
                }}>
                  /{page.slug}
                </p>
                {page.seo_title && (
                  <div style={{
                    background: '#e8f5e8',
                    color: '#2d5a2d',
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    borderRadius: 'var(--border-radius)',
                    fontSize: 'var(--font-size-xs)',
                    marginBottom: 'var(--spacing-2)'
                  }}>
                    ✓ SEO konfigurerad
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: 'var(--spacing-2)',
                  marginTop: 'var(--spacing-3)'
                }}>
                  <button
                    style={{
                      flex: 1,
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      background: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--border-radius)',
                      fontSize: 'var(--font-size-sm)',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      if (onEditSEO) {
                        onEditSEO(page);
                      }
                    }}
                  >
                    Redigera SEO
                  </button>
                  <a
                    href={`/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      background: 'transparent',
                      color: 'var(--color-text)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--border-radius)',
                      fontSize: 'var(--font-size-sm)',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Visa
                  </a>
                </div>
              </div>
            </ProductCard>
          ))}
        </ProductGrid>
      )}
    </div>
  );
};

const AdminSEO: React.FC = () => {
  const [activeTab, setActiveTab] = useState('global');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [globalSEO, setGlobalSEO] = useState<SEOSettings>({
    entity_type: 'global',
    entity_id: 0,
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    canonical_url: '',
    og_title: '',
    og_description: '',
    og_image: '',
    robots_meta: 'index,follow'
  });
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [productSEO, setProductSEO] = useState<{ [key: number]: SEOSettings }>({});
  const [categorySEO, setCategorySEO] = useState<{ [key: number]: SEOSettings }>({});
  const [stats, setStats] = useState({
    totalProducts: 0,
    productsWithSEO: 0,
    totalCategories: 0,
    categoriesWithSEO: 0
  });
  const [seoStatistics, setSEOStatistics] = useState<any>(null);
  const [editingSEO, setEditingSEO] = useState<{
    type: 'product' | 'category' | 'marketing_page';
    entity: any;
    data: SEOSettings;
  } | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Load global SEO settings
      const globalSeoData = await apiService.getSEOSettings('global', 0);
      if (globalSeoData && Array.isArray(globalSeoData) && globalSeoData.length > 0) {
        setGlobalSEO(globalSeoData[0]);
      }
      
      // Load products
      const productsData = await apiService.getProducts();
      setProducts(productsData);
      
      // Load categories
      const categoriesData = await apiService.getCategories();
      setCategories(categoriesData);
      
      // Load all SEO settings
      const allSeoData = await apiService.getSEOSettings();
      
      // Process product SEO
      const prodSEO: { [key: number]: SEOSettings } = {};
      const catSEO: { [key: number]: SEOSettings } = {};
      
      if (Array.isArray(allSeoData)) {
        allSeoData.forEach(seo => {
          if (seo.entity_type === 'product') {
            prodSEO[seo.entity_id] = seo;
          } else if (seo.entity_type === 'category') {
            catSEO[seo.entity_id] = seo;
          }
        });
      }
      
      setProductSEO(prodSEO);
      setCategorySEO(catSEO);
      
      // Calculate stats
      setStats({
        totalProducts: productsData.length,
        productsWithSEO: Object.keys(prodSEO).length,
        totalCategories: categoriesData.length,
        categoriesWithSEO: Object.keys(catSEO).length
      });
      
      // Load SEO statistics for gauges
      try {
        const seoStats = await apiService.getSEOStatistics();
        setSEOStatistics(seoStats);
      } catch (seoError) {
        console.warn('Could not load SEO statistics:', seoError);
      }
      
    } catch (err: any) {
      setError(`Kunde inte ladda SEO-data: ${err.message}`);
      console.error('Error loading SEO data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalSEOChange = (field: string, value: string) => {
    setGlobalSEO(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveGlobalSEO = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      if (globalSEO.id) {
        await apiService.updateSEOSettings(globalSEO.id, globalSEO);
      } else {
        await apiService.saveSEOSettings(globalSEO);
      }
      
      await loadData(); // Reload data to get updated info
    } catch (err: any) {
      setError(`Kunde inte spara SEO-inställningar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getCharCountClass = (length: number, limit: number) => {
    const ratio = length / limit;
    if (ratio > 1) return 'error';
    if (ratio > 0.9) return 'warning';
    return 'good';
  };

  const generateProductSEO = (product: any) => {
    const categoryText = product.categories && product.categories.length > 0 
      ? ` i kategorin ${product.categories.join(', ')}` 
      : '';
    
    return {
      meta_title: `${product.name} | Lillaflora`,
      meta_description: `Köp ${product.name}${categoryText}. ${product.short_description || 'Högkvalitativa produkter med snabb leverans.'}`,
      canonical_url: `https://lillaflora.nu/produkter/${product.slug}`,
      og_title: product.name,
      og_description: product.short_description || product.description?.substring(0, 160) || '',
    };
  };

  const generateCategorySEO = (category: any) => {
    return {
      meta_title: `${category.name} | Lillaflora`,
      meta_description: `Utforska vårt sortiment av ${category.name.toLowerCase()}. Högkvalitativa produkter med snabb leverans.`,
      canonical_url: `https://lillaflora.nu/kategorier/${category.slug}`,
      og_title: `${category.name} - Lillaflora`,
      og_description: `Upptäck våra ${category.name.toLowerCase()} - perfekt för att skapa mys och stämning i ditt hem.`,
    };
  };

  const generateMarketingPageSEO = (page: any) => {
    return {
      meta_title: page.seo_title || `${page.title} | Lillaflora`,
      meta_description: page.seo_description || page.excerpt || `Upptäck ${page.title}. Högkvalitativa produkter med snabb leverans från Lillaflora.`,
      canonical_url: `https://lillaflora.nu/${page.slug}`,
      og_title: page.seo_title || page.title,
      og_description: page.seo_description || page.excerpt || '',
    };
  };

  const handleEditMarketingPageSEO = (page: any) => {
    openSEOEditor('marketing_page', page);
  };

  const groupProductsByCategory = () => {
    const grouped: { [categoryName: string]: any[] } = {};
    
    products.forEach(product => {
      if (product.categories && product.categories.length > 0) {
        product.categories.forEach((categoryName: string) => {
          if (!grouped[categoryName]) {
            grouped[categoryName] = [];
          }
          grouped[categoryName].push(product);
        });
      } else {
        // Products without categories go to "Okategoriserade"
        if (!grouped['Okategoriserade']) {
          grouped['Okategoriserade'] = [];
        }
        grouped['Okategoriserade'].push(product);
      }
    });

    return grouped;
  };

  const openSEOEditor = (type: 'product' | 'category' | 'marketing_page', entity: any) => {
    let existingSEO, autoSEO;

    if (type === 'product') {
      existingSEO = productSEO[entity.id];
      autoSEO = generateProductSEO(entity);
    } else if (type === 'category') {
      existingSEO = categorySEO[entity.id];
      autoSEO = generateCategorySEO(entity);
    } else if (type === 'marketing_page') {
      // Use existing SEO data from the marketing page itself
      existingSEO = {
        entity_type: 'marketing_page',
        entity_id: entity.id,
        meta_title: entity.seo_title || '',
        meta_description: entity.seo_description || '',
        meta_keywords: entity.seo_keywords || '',
        canonical_url: `https://lillaflora.nu/${entity.slug}`,
        og_title: entity.seo_title || entity.title,
        og_description: entity.seo_description || entity.excerpt || '',
        og_image: entity.featured_image || '',
        robots_meta: 'index,follow'
      };
      autoSEO = generateMarketingPageSEO(entity);
    }
    
    const seoData = existingSEO || {
      entity_type: type,
      entity_id: entity.id,
      meta_title: autoSEO?.meta_title || '',
      meta_description: autoSEO?.meta_description || '',
      meta_keywords: '',
      canonical_url: autoSEO?.canonical_url || '',
      og_title: autoSEO?.og_title || '',
      og_description: autoSEO?.og_description || '',
      og_image: '',
      robots_meta: 'index,follow'
    };

    setEditingSEO({ type, entity, data: seoData });
    setEditingTitle(type === 'marketing_page' ? entity.title : entity.name);
  };

  const handleSEOChange = (field: string, value: string) => {
    if (editingSEO) {
      setEditingSEO({
        ...editingSEO,
        data: {
          ...editingSEO.data,
          [field]: value
        }
      });
    }
  };

  const handleSaveSEO = async () => {
    if (!editingSEO) return;
    
    setSaving(true);
    setError('');
    
    try {
      if (editingSEO.data.id) {
        await apiService.updateSEOSettings(editingSEO.data.id, editingSEO.data);
      } else {
        await apiService.saveSEOSettings(editingSEO.data);
      }

      // If this is a marketing page, also update the marketing page with the SEO data
      if (editingSEO.type === 'marketing_page') {
        const updatedPageData = {
          ...editingSEO.entity,
          seo_title: editingSEO.data.meta_title,
          seo_description: editingSEO.data.meta_description,
          seo_keywords: editingSEO.data.meta_keywords
        };
        await marketingService.update(editingSEO.entity.id, updatedPageData);
      }

      setEditingSEO(null);
      await loadData(); // Reload data to get updated info
    } catch (err: any) {
      setError(`Kunde inte spara SEO-inställningar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'global', label: 'Globala inställningar', icon: '🌐' },
    { id: 'products', label: 'Produkter', icon: '📦' },
    { id: 'categories', label: 'Kategorier', icon: '📁' },
    { id: 'marketing-pages', label: 'Marknadsföringssidor', icon: '📄' },
    { id: 'overview', label: 'Översikt', icon: '📊' }
  ];

  if (loading) {
    return (
      <SEOWrapper>
        <SEOHeader>
          <h2>🔍 SEO-inställningar</h2>
          <p>Hantera metadata, sökoptimering och social media-inställningar</p>
        </SEOHeader>
        <LoadingWrapper>Laddar SEO-data...</LoadingWrapper>
      </SEOWrapper>
    );
  }

  return (
    <SEOWrapper>
      <SEOHeader>
        <h2>🔍 SEO-inställningar</h2>
        <p>Optimera din webbplats för sökmotorer och sociala medier</p>
      </SEOHeader>
      
      {error && <ErrorWrapper>{error}</ErrorWrapper>}
      
      <TabNavigation>
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} {tab.label}
          </div>
        ))}
      </TabNavigation>
      
      <TabContent>
        {activeTab === 'overview' && (
          <div>
            <h3>SEO Översikt</h3>
            
            {seoStatistics ? (
              <SEOCategoryGauges
                overview={seoStatistics.overview}
                categoryStats={seoStatistics.by_category}
              />
            ) : (
              <Stats>
                <StatCard>
                  <div className="stat-number">{stats.totalProducts}</div>
                  <div className="stat-label">Totala produkter</div>
                </StatCard>
                <StatCard>
                  <div className="stat-number">{stats.productsWithSEO}</div>
                  <div className="stat-label">Produkter med SEO</div>
                </StatCard>
                <StatCard>
                  <div className="stat-number">{stats.totalCategories}</div>
                  <div className="stat-label">Totala kategorier</div>
                </StatCard>
                <StatCard>
                  <div className="stat-number">{stats.categoriesWithSEO}</div>
                  <div className="stat-label">Kategorier med SEO</div>
                </StatCard>
              </Stats>
            )}
            
            <p style={{ color: 'var(--color-text-light)', marginTop: 'var(--spacing-6)' }}>
              SEO (Search Engine Optimization) hjälper din webbplats att rankas högre i sökresultat. 
              Fyll i meningsfulla titlar och beskrivningar för alla produkter och kategorier.
            </p>
          </div>
        )}

        {activeTab === 'global' && (
          <SEOForm onSubmit={handleSaveGlobalSEO}>
            <FormSection>
              <h4>🔍 Grundläggande SEO</h4>
              
              <FormGroup>
                <label>Meta Titel</label>
                <input
                  type="text"
                  value={globalSEO.meta_title}
                  onChange={(e) => handleGlobalSEOChange('meta_title', e.target.value)}
                  maxLength={60}
                  placeholder="Lillaflora - Svenska Inredningsdetaljer"
                />
                <div className={`char-count ${getCharCountClass(globalSEO.meta_title.length, 60)}`}>
                  {globalSEO.meta_title.length}/60 tecken
                </div>
                <div className="help-text">
                  Visas som titel i sökresultat och webbläsarflik. Ska vara unik och beskriva sidan.
                </div>
              </FormGroup>
              
              <FormGroup>
                <label>Meta Beskrivning</label>
                <textarea
                  value={globalSEO.meta_description}
                  onChange={(e) => handleGlobalSEOChange('meta_description', e.target.value)}
                  maxLength={160}
                  placeholder="Upptäck vårt sortiment av ljus, doftljus och andra vackra inredningsdetaljer..."
                />
                <div className={`char-count ${getCharCountClass(globalSEO.meta_description.length, 160)}`}>
                  {globalSEO.meta_description.length}/160 tecken
                </div>
                <div className="help-text">
                  Visas som beskrivning under titeln i sökresultat. Ska locka användare att klicka.
                </div>
              </FormGroup>
              
              <FormGroup>
                <label>Meta Nyckelord</label>
                <input
                  type="text"
                  value={globalSEO.meta_keywords}
                  onChange={(e) => handleGlobalSEOChange('meta_keywords', e.target.value)}
                  placeholder="inredning, ljus, doftljus, svenska, hemkänsla"
                />
                <div className="help-text">
                  Kommaseparerade nyckelord som beskriver din webbplats innehåll.
                </div>
              </FormGroup>
              
              <FormGroup>
                <label>Kanonisk URL</label>
                <input
                  type="url"
                  value={globalSEO.canonical_url}
                  onChange={(e) => handleGlobalSEOChange('canonical_url', e.target.value)}
                  placeholder="https://lillaflora.nu"
                />
                <div className="help-text">
                  Den officiella URL:en för din webbplats. Hjälper till att undvika dublerat innehåll.
                </div>
              </FormGroup>
              
              <FormGroup>
                <label>Robots Meta</label>
                <select
                  value={globalSEO.robots_meta}
                  onChange={(e) => handleGlobalSEOChange('robots_meta', e.target.value)}
                >
                  <option value="index,follow">Index, Follow (Rekommenderat)</option>
                  <option value="index,nofollow">Index, No Follow</option>
                  <option value="noindex,follow">No Index, Follow</option>
                  <option value="noindex,nofollow">No Index, No Follow</option>
                </select>
                <div className="help-text">
                  Instruktioner till sökmotorer om hur de ska indexera din sida.
                </div>
              </FormGroup>
            </FormSection>
            
            <FormSection>
              <h4>📱 Social Media (Open Graph)</h4>
              
              <FormGroup>
                <label>Open Graph Titel</label>
                <input
                  type="text"
                  value={globalSEO.og_title}
                  onChange={(e) => handleGlobalSEOChange('og_title', e.target.value)}
                  maxLength={60}
                  placeholder="Lillaflora - Svenska Inredningsdetaljer"
                />
                <div className="help-text">Titel som visas när sidan delas på sociala medier</div>
              </FormGroup>
              
              <FormGroup>
                <label>Open Graph Beskrivning</label>
                <textarea
                  value={globalSEO.og_description}
                  onChange={(e) => handleGlobalSEOChange('og_description', e.target.value)}
                  maxLength={160}
                  placeholder="Upptäck vårt sortiment av ljus och inredningsdetaljer..."
                />
                <div className="help-text">Beskrivning som visas när sidan delas på sociala medier</div>
              </FormGroup>
              
              <FormGroup>
                <label>Open Graph Bild URL</label>
                <input
                  type="url"
                  value={globalSEO.og_image}
                  onChange={(e) => handleGlobalSEOChange('og_image', e.target.value)}
                  placeholder="https://lillaflora.nu/og-image.jpg"
                />
                <div className="help-text">
                  Bild som visas när sidan delas på sociala medier (rekommenderad storlek: 1200x630px)
                </div>
              </FormGroup>
            </FormSection>

            <SEOPreview>
              <h5>🔍 Förhandsgranskning i sökresultat:</h5>
              <div className="preview-title">{globalSEO.meta_title || 'Titel saknas'}</div>
              <div className="preview-url">{globalSEO.canonical_url || 'https://lillaflora.nu'}</div>
              <div className="preview-description">
                {globalSEO.meta_description || 'Beskrivning saknas'}
              </div>
            </SEOPreview>
            
            <SaveButton type="submit" disabled={saving}>
              {saving ? '💾 Sparar...' : '💾 Spara globala inställningar'}
            </SaveButton>
          </SEOForm>
        )}
        
        {activeTab === 'products' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-6)' }}>
              <div>
                <h3>📦 Produkt SEO-inställningar</h3>
                <p style={{ color: 'var(--color-text-light)' }}>
                  Optimera SEO för varje produkt. Produkter utan SEO använder automatiskt genererade titlar och beskrivningar.
                </p>
              </div>
            </div>
            
            {Object.entries(groupProductsByCategory()).map(([categoryName, categoryProducts]) => (
              <CategorySection key={categoryName}>
                <h4>
                  📁 {categoryName} ({categoryProducts.length} produkt{categoryProducts.length !== 1 ? 'er' : ''})
                </h4>
                <ProductGrid>
                  {categoryProducts.map((product: any) => {
                    const hasSEO = productSEO[product.id];
                    const autoSEO = generateProductSEO(product);
                    
                    return (
                      <ProductCard key={product.id} hasSEO={!!hasSEO}>
                        <div className="product-header">
                          <div className="product-info">
                            <div className="product-name">{product.name}</div>
                            <div className="product-url">
                              /produkter/{product.slug}
                            </div>
                          </div>
                          <div className={`seo-badge ${hasSEO ? 'has-seo' : 'no-seo'}`}>
                            {hasSEO ? '✅ Har SEO' : '⚠️ Auto SEO'}
                          </div>
                        </div>
                        
                        <div className="seo-preview">
                          <div className="title">
                            {hasSEO ? hasSEO.meta_title : autoSEO.meta_title}
                          </div>
                          <div className="description">
                            {hasSEO ? hasSEO.meta_description : autoSEO.meta_description}
                          </div>
                        </div>
                        
                        <button 
                          className="edit-btn"
                          onClick={() => openSEOEditor('product', product)}
                        >
                          {hasSEO ? 'Redigera SEO' : 'Lägg till SEO'}
                        </button>
                      </ProductCard>
                    );
                  })}
                </ProductGrid>
              </CategorySection>
            ))}
          </div>
        )}


        {activeTab === 'categories' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-6)' }}>
              <div>
                <h3>📁 Kategori SEO-inställningar</h3>
                <p style={{ color: 'var(--color-text-light)' }}>
                  Optimera SEO för varje kategori. Kategorier utan SEO använder automatiskt genererade titlar och beskrivningar.
                </p>
              </div>
            </div>
            
            <ProductGrid>
              {categories.map(category => {
                const hasSEO = categorySEO[category.id];
                const autoSEO = generateCategorySEO(category);
                
                return (
                  <ProductCard key={category.id} hasSEO={!!hasSEO}>
                    <div className="product-header">
                      <div className="product-info">
                        <div className="product-name">{category.name}</div>
                        <div className="product-url">
                          /kategorier/{category.slug}
                        </div>
                      </div>
                      <div className={`seo-badge ${hasSEO ? 'has-seo' : 'no-seo'}`}>
                        {hasSEO ? '✅ Har SEO' : '⚠️ Auto SEO'}
                      </div>
                    </div>
                    
                    <div className="seo-preview">
                      <div className="title">
                        {hasSEO ? hasSEO.meta_title : autoSEO.meta_title}
                      </div>
                      <div className="description">
                        {hasSEO ? hasSEO.meta_description : autoSEO.meta_description}
                      </div>
                    </div>
                    
                    <button 
                      className="edit-btn"
                      onClick={() => openSEOEditor('category', category)}
                    >
                      {hasSEO ? 'Redigera SEO' : 'Lägg till SEO'}
                    </button>
                  </ProductCard>
                );
              })}
            </ProductGrid>
          </div>
        )}

        {activeTab === 'marketing-pages' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-6)' }}>
              <div>
                <h3>📄 Marknadsföringssidor SEO</h3>
                <p style={{ color: 'var(--color-text-light)' }}>
                  Hantera SEO-inställningar för dina marknadsföringssidor. Varje sida kan ha anpassade meta-titlar, beskrivningar och nyckelord.
                </p>
              </div>
            </div>

            <MarketingPagesSection onEditSEO={handleEditMarketingPageSEO} />
          </div>
        )}
      </TabContent>
      
      {/* SEO Editing Modal */}
      {editingSEO && (
        <ModalOverlay onClick={() => setEditingSEO(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>
                {editingSEO.type === 'product' ? '📦' : editingSEO.type === 'marketing_page' ? '📄' : '📁'}
                SEO för {editingTitle}
              </h3>
              <button className="close-btn" onClick={() => setEditingSEO(null)}>
                ×
              </button>
            </ModalHeader>
            
            <ModalBody>
              {error && <ErrorWrapper>{error}</ErrorWrapper>}
              
              <SEOForm onSubmit={(e) => { e.preventDefault(); handleSaveSEO(); }}>
                <FormSection>
                  <h4>🔍 Grundläggande SEO</h4>
                  
                  <FormGroup>
                    <label>Meta Titel</label>
                    <input
                      type="text"
                      value={editingSEO.data.meta_title}
                      onChange={(e) => handleSEOChange('meta_title', e.target.value)}
                      maxLength={60}
                      placeholder="Skapades automatiskt baserat på produktnamn och kategori"
                    />
                    <div className={`char-count ${getCharCountClass(editingSEO.data.meta_title.length, 60)}`}>
                      {editingSEO.data.meta_title.length}/60 tecken
                    </div>
                    <div className="help-text">
                      Visas som titel i sökresultat och webbläsarflik. Lämna tom för automatisk generering.
                    </div>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Meta Beskrivning</label>
                    <textarea
                      value={editingSEO.data.meta_description}
                      onChange={(e) => handleSEOChange('meta_description', e.target.value)}
                      maxLength={160}
                      placeholder="Skapades automatiskt baserat på produktbeskrivning och kategori"
                    />
                    <div className={`char-count ${getCharCountClass(editingSEO.data.meta_description.length, 160)}`}>
                      {editingSEO.data.meta_description.length}/160 tecken
                    </div>
                    <div className="help-text">
                      Visas som beskrivning under titeln i sökresultat. Lämna tom för automatisk generering.
                    </div>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Meta Nyckelord</label>
                    <input
                      type="text"
                      value={editingSEO.data.meta_keywords}
                      onChange={(e) => handleSEOChange('meta_keywords', e.target.value)}
                      placeholder="inredning, ljus, doftljus, kategori"
                    />
                    <div className="help-text">
                      Kommaseparerade nyckelord som beskriver denna {editingSEO.type === 'product' ? 'produkt' : 'kategori'}.
                    </div>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Kanonisk URL</label>
                    <input
                      type="url"
                      value={editingSEO.data.canonical_url}
                      onChange={(e) => handleSEOChange('canonical_url', e.target.value)}
                      placeholder={`https://lillaflora.nu/${editingSEO.type === 'product' ? 'produkter' : 'kategorier'}/${editingSEO.entity.slug}`}
                    />
                    <div className="help-text">
                      Den officiella URL:en för denna sida. Lämna tom för automatisk generering.
                    </div>
                  </FormGroup>
                </FormSection>
                
                <FormSection>
                  <h4>📱 Social Media (Open Graph)</h4>
                  
                  <FormGroup>
                    <label>Open Graph Titel</label>
                    <input
                      type="text"
                      value={editingSEO.data.og_title}
                      onChange={(e) => handleSEOChange('og_title', e.target.value)}
                      maxLength={60}
                      placeholder={editingSEO.entity.name}
                    />
                    <div className="help-text">Titel som visas när sidan delas på sociala medier</div>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Open Graph Beskrivning</label>
                    <textarea
                      value={editingSEO.data.og_description}
                      onChange={(e) => handleSEOChange('og_description', e.target.value)}
                      maxLength={160}
                      placeholder="Beskrivning för sociala medier..."
                    />
                    <div className="help-text">Beskrivning som visas när sidan delas på sociala medier</div>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Open Graph Bild URL</label>
                    <input
                      type="url"
                      value={editingSEO.data.og_image}
                      onChange={(e) => handleSEOChange('og_image', e.target.value)}
                      placeholder="https://lillaflora.nu/images/og-product.jpg"
                    />
                    <div className="help-text">
                      Bild som visas när sidan delas på sociala medier (rekommenderad storlek: 1200x630px)
                    </div>
                  </FormGroup>
                </FormSection>

                <SEOPreview>
                  <h5>🔍 Förhandsgranskning i sökresultat:</h5>
                  <div className="preview-title">{editingSEO.data.meta_title || `${editingSEO.entity.name} | Lillaflora`}</div>
                  <div className="preview-url">{editingSEO.data.canonical_url || `https://lillaflora.nu/${editingSEO.type === 'product' ? 'produkter' : 'kategorier'}/${editingSEO.entity.slug}`}</div>
                  <div className="preview-description">
                    {editingSEO.data.meta_description || (editingSEO.type === 'product' ? generateProductSEO(editingSEO.entity).meta_description : generateCategorySEO(editingSEO.entity).meta_description)}
                  </div>
                </SEOPreview>
              </SEOForm>
            </ModalBody>
            
            <ModalFooter>
              <button 
                type="button" 
                className="secondary" 
                onClick={() => setEditingSEO(null)}
              >
                Avbryt
              </button>
              <button 
                type="button" 
                className="primary" 
                onClick={handleSaveSEO}
                disabled={saving}
              >
                {saving ? '💾 Sparar...' : '💾 Spara SEO'}
              </button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </SEOWrapper>
  );
};

export default AdminSEO;